import { useCallback, useEffect, useState } from "react";
import { todayKey } from "../utils/dateWindow";

/**
 * The day every rolling window resolves against, plus a signal for when that
 * day has moved on.
 *
 * A dashboard tab left open across local midnight is the case that matters. The
 * two obvious options both fail silently: never re-resolving leaves you on a
 * window that ends yesterday, and re-resolving automatically moves the ground
 * under someone who may be reading a number off the chart. So this detects the
 * rollover and reports it — `isStale` drives a control, and nothing moves until
 * `refresh()` is called (design doc 033, decision A1).
 *
 * `resolvedFor` is deliberately pinned rather than tracking "now": it is the day
 * the currently-displayed data was resolved against, so memoising resolution on
 * it keeps query params stable between renders. Recomputing `new Date()` per
 * render would produce a new range object every time and refetch in a loop.
 */
export function useTodayKey() {
  const [resolvedFor, setResolvedFor] = useState(() => todayKey());
  const [liveKey, setLiveKey] = useState(resolvedFor);

  useEffect(() => {
    const check = () => setLiveKey(todayKey());
    // Both: `focus` covers returning to a background tab, `visibilitychange`
    // covers a machine waking from sleep with the tab already focused.
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  const refresh = useCallback(() => setResolvedFor(todayKey()), []);

  return {
    /** The day to resolve windows against. Stable until `refresh()`. */
    todayKey: resolvedFor,
    /** True once the local date has moved past the resolved day. */
    isStale: liveKey !== resolvedFor,
    refresh,
  };
}
